// useFieldModalState.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Hooks
import useEnumOptionEditor from 'admin/nodetypes/fields/hooks/useEnumOptionEditor';

// Utilities
import {
    DEFAULT_VALUE_UNSUPPORTED_TYPES,
    FIELD_TYPE_DESCRIPTIONS,
    FIELD_TYPES,
    FIELD_TYPE_LABELS,
    ONCE_ONLY_FIELD_TYPES,
    makeDraft,
} from '../constants';
import { buildDraftFromField, buildNextFieldFromDraft, patchDraftWithRules } from '../fieldDraft';
import { validateDraft } from '../validation';

const FIELD_TYPE_ICON_KEYS = {
    string: 'title',
    barcode: 'barcode',
    phone: 'call',
    email: 'mail',
    measurement: 'straighten',
    enum: 'list_alt',
    people: 'group',
    date: 'calendar_today',
    daterange: 'date_range',
    datetime: 'schedule',
    datetimerange: 'event_repeat',
    cover: 'image',
    color: 'palette',
    description: 'description',
    location: 'location_on',
    boolean: 'toggle_on',
    number: 'tag',
    percentage: 'percent',
    currency: 'attach_money',
    nodelist: 'format_list_bulleted',
    url: 'link',
    checklist: 'checklist',
    tags: 'sell',
    comments: 'chat',
    activity: 'timeline',
    richtext: 'article',
};

const EMPTY_ERRORS = {
    type: '',
    label: '',
    options: '',
    snapTo: '',
    targetType: '',
    defaultUnit: '',
};

const sortTypeOptionsByLabel = (options) => (
    [...options].sort((left, right) => (
        String(left?.label || '').localeCompare(String(right?.label || ''), undefined, {
            sensitivity: 'base',
        })
    ))
);

const areEnumOptionListsEqual = (leftOptions, rightOptions) => {
    const left = Array.isArray(leftOptions) ? leftOptions : [];
    const right = Array.isArray(rightOptions) ? rightOptions : [];
    if (left.length !== right.length) return false;

    for (let index = 0; index < left.length; index += 1) {
        const leftOption = left[index] || {};
        const rightOption = right[index] || {};

        if (String(leftOption.id || '') !== String(rightOption.id || '')) return false;
        if (String(leftOption.value || '') !== String(rightOption.value || '')) return false;
        if (String(leftOption.color || '') !== String(rightOption.color || '')) return false;
        if (String(leftOption.iconKey || '') !== String(rightOption.iconKey || '')) return false;

        const leftEnLabel = String(leftOption?.translations?.en?.label || '').trim();
        const rightEnLabel = String(rightOption?.translations?.en?.label || '').trim();
        if (leftEnLabel !== rightEnLabel) return false;
    }

    return true;
};

const areDraftsEqual = (leftDraft, rightDraft) => {
    const left = leftDraft && typeof leftDraft === 'object' ? leftDraft : {};
    const right = rightDraft && typeof rightDraft === 'object' ? rightDraft : {};

    if (String(left.label || '') !== String(right.label || '')) return false;
    if (String(left.fieldDescription || '') !== String(right.fieldDescription || '')) return false;
    if (String(left.type || '') !== String(right.type || '')) return false;
    if (String(left.targetType || '') !== String(right.targetType || '')) return false;
    if (String(left.default ?? '') !== String(right.default ?? '')) return false;
    if (String(left.defaultUnit || '') !== String(right.defaultUnit || '')) return false;
    if (String(left.enumOptionsSort || '') !== String(right.enumOptionsSort || '')) return false;
    if (String(left.snapTo ?? '') !== String(right.snapTo ?? '')) return false;

    return areEnumOptionListsEqual(left.options, right.options);
};

/**
 * useFieldModalState
 *
 * Manages draft state, derived options, enum-option editing, and save validation for FieldModal.
 *
 * @param {object} params - Hook parameters.
 * @param {boolean} params.isOpen - Whether the modal is open.
 * @param {function} params.onSave - Callback when the field is saved.
 * @param {object|null} [params.editingField=null] - Existing field being edited.
 * @param {string} [params.nodeTypeLabel=''] - Display label for the current node type.
 * @param {array} [params.existingFields=[]] - Existing schema fields for once-only checks.
 * @param {string[]|null} [params.allowedFieldTypes=null] - Optional allowlist of visible field types.
 *
 * @returns {object} Grouped state and handlers for the FieldModal shell.
 */
export default function useFieldModalState({
    isOpen,
    onSave,
    editingField = null,
    nodeTypeLabel = '',
    existingFields = [],
    allowedFieldTypes = null,
}) {
    const { getAll, getLabelPlural } = useNodeTypes();

    const [draft, setDraft] = useState(makeDraft);
    const [errors, setErrors] = useState(EMPTY_ERRORS);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const isEditing = Boolean(editingField);

    const resolvedAllowedFieldTypes = useMemo(() => {
        if (!Array.isArray(allowedFieldTypes) || allowedFieldTypes.length === 0) {
            return FIELD_TYPES;
        }

        const allowedSet = new Set(
            allowedFieldTypes
                .map((value) => String(value || '').trim())
                .filter(Boolean)
        );

        return FIELD_TYPES.filter((value) => allowedSet.has(value));
    }, [allowedFieldTypes]);

    const unavailableOnceOnlyTypes = useMemo(() => {
        if (!Array.isArray(existingFields)) return [];

        return ONCE_ONLY_FIELD_TYPES.filter((type) => (
            existingFields.some((field) => (
                field?.type === type && field?.name !== editingField?.name
            ))
        ));
    }, [editingField?.name, existingFields]);

    const typeOptions = useMemo(() => (
        sortTypeOptionsByLabel(
            resolvedAllowedFieldTypes
                .filter((value) => isEditing || !unavailableOnceOnlyTypes.includes(value))
                .map((value) => ({
                    value,
                    label: FIELD_TYPE_LABELS[value] || value,
                    iconKey: FIELD_TYPE_ICON_KEYS[value] || 'category',
                    note: FIELD_TYPE_DESCRIPTIONS[value] || '',
                }))
        )
    ), [isEditing, resolvedAllowedFieldTypes, unavailableOnceOnlyTypes]);

    const mergedTypeOptions = useMemo(() => {
        if (!isEditing || !draft.type) return typeOptions;
        if (resolvedAllowedFieldTypes.includes(draft.type)) return typeOptions;

        return sortTypeOptionsByLabel([
            ...typeOptions,
            {
                value: draft.type,
                label: FIELD_TYPE_LABELS[draft.type] || draft.type,
                iconKey: FIELD_TYPE_ICON_KEYS[draft.type] || 'category',
                note: FIELD_TYPE_DESCRIPTIONS[draft.type] || '',
            },
        ]);
    }, [draft.type, isEditing, resolvedAllowedFieldTypes, typeOptions]);

    const targetTypeOptions = useMemo(() => {
        const typeRows = Array.isArray(getAll?.()) ? getAll() : [];

        return typeRows
            .map((type) => {
                const key = String(type?.key || '').trim();
                if (!key || key.toUpperCase() === 'USER' || type?.system === true) return null;

                return {
                    value: key,
                    label: getLabelPlural(key, 'en', { fallback: key }),
                };
            })
            .filter(Boolean)
            .sort((left, right) => String(left.label || '').localeCompare(String(right.label || '')));
    }, [getAll, getLabelPlural]);

    /**
     * patchDraft
     *
     * Applies a partial draft update while preserving field-type-specific draft rules.
     *
     * @param {object} patch - Partial field draft values to merge into the current draft.
     */
    const patchDraft = useCallback((patch) => {
        if (!patch || typeof patch !== 'object') return;
        setDraft((prev) => patchDraftWithRules({ prevDraft: prev, patch, isEditing }));
    }, [isEditing]);

    const {
        selectedEnumOptions,
        optionValueDrafts,
        setOptionValueDrafts,
        handleOptionAdd,
        handleOptionRemove,
        handleOptionValueCommit,
        handleOptionValueKeyDown,
        handleOptionColorChange,
        handleOptionIconChange,
        handleOptionMove,
    } = useEnumOptionEditor({
        selected: draft,
        selectedIndex: 0,
        patchSelected: patchDraft,
    });

    const isEnumType = draft.type === 'enum';
    const isMeasurementType = draft.type === 'measurement';
    const isOptionsType = draft.type === 'enum' || draft.type === 'multiselect' || isMeasurementType;
    const isNodeListType = draft.type === 'nodelist';
    const isTagsType = draft.type === 'tags';
    const isDefaultUnsupported = DEFAULT_VALUE_UNSUPPORTED_TYPES.includes(draft.type);
    const resolvedNodeTypeLabel = typeof nodeTypeLabel === 'string' ? nodeTypeLabel.trim() : '';

    const modalTitle = useMemo(() => {
        if (!resolvedNodeTypeLabel) {
            return isEditing ? 'Edit Field' : 'Add Field';
        }

        return isEditing
            ? `Edit "${resolvedNodeTypeLabel}" Field`
            : `Add Field to "${resolvedNodeTypeLabel}"`;
    }, [isEditing, resolvedNodeTypeLabel]);

    useEffect(() => {
        if (!isOpen) return;

        const nextDraft = isEditing
            ? buildDraftFromField(editingField)
            : makeDraft();

        setDraft((prev) => (areDraftsEqual(prev, nextDraft) ? prev : nextDraft));

        setErrors((prev) => {
            const previous = prev && typeof prev === 'object' ? prev : EMPTY_ERRORS;
            if (
                String(previous.type || '') === ''
                && String(previous.label || '') === ''
                && String(previous.options || '') === ''
                && String(previous.snapTo || '') === ''
                && String(previous.targetType || '') === ''
                && String(previous.defaultUnit || '') === ''
            ) {
                return prev;
            }

            return EMPTY_ERRORS;
        });
    }, [editingField, isEditing, isOpen]);

    /**
     * clearError
     *
     * Clears one validation error entry after the user edits that field.
     *
     * @param {string} errorKey - Error key to reset.
     */
    const clearError = useCallback((errorKey) => {
        setErrors((prev) => ({
            ...prev,
            [errorKey]: '',
        }));
    }, []);

    /**
     * handleSave
     *
     * Validates the current draft and emits the normalized field definition when valid.
     */
    const handleSave = useCallback(() => {
        const validation = validateDraft(draft, {
            unavailableTypes: unavailableOnceOnlyTypes,
        });

        if (!validation.isValid) {
            setErrors(validation.errors);
            return;
        }

        const nextField = buildNextFieldFromDraft(
            draft.type === 'enum'
                ? { ...draft, enumOptionsSort: 'manual' }
                : draft
        );

        if (typeof onSave === 'function') {
            onSave(nextField);
        }
    }, [draft, onSave, unavailableOnceOnlyTypes]);

    const canDelete = isEditing && !editingField?.inherited;
    const deleteLabel = editingField?.label || 'this field';

    return {
        modalState: {
            modalTitle,
            isEditing,
            canDelete,
            deleteLabel,
            confirmDeleteOpen,
            setConfirmDeleteOpen,
        },
        formState: {
            draft,
            errors,
            mergedTypeOptions,
            targetTypeOptions,
            isEnumType,
            isMeasurementType,
            isOptionsType,
            isNodeListType,
            isTagsType,
            isDefaultUnsupported,
            patchDraft,
            clearError,
            handleSave,
        },
        enumOptionsState: {
            selectedEnumOptions,
            optionValueDrafts,
            setOptionValueDrafts,
            handleOptionAdd,
            handleOptionRemove,
            handleOptionValueCommit,
            handleOptionValueKeyDown,
            handleOptionColorChange,
            handleOptionIconChange,
            handleOptionMove,
        },
    };
}